import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import SearchInput from "../common/search_input/SearchInput";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import ToolsCheckbox from "../clients/Tools/kit/ToolsCheckbox";
import ToolsCallout from "../clients/Tools/kit/ToolsCallout";
import ToolLibraryService from "../../services/ToolLibraryService";
import UtilityService from "../../services/UtilityService";
import AgentSourceIcon from "./AgentSourceIcon";
import AgentToolIcon from "./AgentToolIcon";

import "./agent-toolbox.css";

const ALWAYS_GROUP_KEY = "always";

const EFFECT_ICONS = {
	reads_only: Icon.Eye,
	proposes: Icon.Edit3,
	produces_file: Icon.FilePlus,
	execute: Icon.Zap,
};

class AgentToolbox extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			tools: [],
			isLoading: true,
			loadError: null,
			search: "",
			category: "",
			effect: "",
			isOnlyUsed: false,
			openGroupKeys: [],
		};
	}

	componentDidMount() {
		this.fetchToolbox();
	}

	fetchToolbox = async () => {
		try {
			const tools = await ToolLibraryService.listToolbox();
			await this.update({ tools, isLoading: false, loadError: null });
		} catch (error) {
			await this.update({ isLoading: false, loadError: UtilityService.describeRequestError({ error, fallback: "The toolbox could not be loaded." }) });
		}
	};

	onToggleGroup = ({ groupKey }) => {
		const { openGroupKeys } = this.state;
		const isOpen = openGroupKeys.includes(groupKey);
		const next = isOpen ? openGroupKeys.filter(key => key !== groupKey) : [...openGroupKeys, groupKey];
		this.update({ openGroupKeys: next });
	};

	isNarrowed() {
		const { search, category, effect, isOnlyUsed } = this.state;
		const isNarrowed = search.trim().length > 0 || !!category || !!effect || isOnlyUsed;
		return isNarrowed;
	}

	onClearNarrowing = () => {
		this.update({ search: "", category: "", effect: "", isOnlyUsed: false });
	};

	renderEffectBadge({ effect }) {
		const described = ToolLibraryService.describeToolEffect({ effect });
		const EffectIcon = EFFECT_ICONS[effect] || Icon.Circle;
		return (
			<span className={`tk-badge tk-badge--${described.tone} agent-toolbox__effect`} title={described.detail}>
				<EffectIcon size={11} />
				{described.label}
			</span>
		);
	}

	renderTool = tool => {
		const usage = ToolLibraryService.describeToolUsage({ usedBySkillCount: tool.usedBySkillCount, usedByPlaybookCount: tool.usedByPlaybookCount });
		return (
			<div key={tool.name} className="agent-toolbox__tool">
				<span className="agent-toolbox__glyph">
					<AgentToolIcon tool={tool} />
				</span>
				<div className="agent-toolbox__text">
					<span className="agent-toolbox__label">{tool.label}</span>
					<span className="agent-toolbox__summary" title={tool.description}>
						{ToolLibraryService.toolSummary({ description: tool.description })}
					</span>
				</div>
				<span className="tk-chip tk-chip--muted agent-toolbox__category">{tool.category}</span>
				{this.renderEffectBadge({ effect: tool.effect })}
				<span className="agent-toolbox__usage">{usage}</span>
			</div>
		);
	};

	renderGroup = group => {
		const { openGroupKeys } = this.state;
		const groupKey = group.key || ALWAYS_GROUP_KEY;
		const isCollapsed = !openGroupKeys.includes(groupKey) && !this.isNarrowed();
		const sourceKey = group.tools[0].needs;

		return (
			<section key={groupKey} className={clsx("agent-toolbox__group", "tk-card", isCollapsed && "agent-toolbox__group--collapsed")}>
				<button
					type="button"
					className="agent-toolbox__group-head"
					onClick={() => this.onToggleGroup({ groupKey })}
					aria-expanded={!isCollapsed}
					title={isCollapsed ? "Show these tools" : "Hide these tools"}
				>
					<span className="tk-icon-tile">{sourceKey ? <AgentSourceIcon icon={sourceKey} size="medium" /> : <Icon.Tool />}</span>
					<span className="agent-toolbox__group-text">
						<span className="agent-toolbox__group-title">
							{group.label}
							<span className="tk-badge tk-badge--neutral">{UtilityService.describeCount({ count: group.tools.length, singular: "tool" })}</span>
						</span>
						{group.reads && <span className="agent-toolbox__group-reads">{group.reads}</span>}
					</span>
					<Icon.ChevronDown size={16} className="agent-toolbox__group-chevron" />
				</button>
				{!isCollapsed && group.tools.map(this.renderTool)}
			</section>
		);
	};

	renderRail() {
		const legend = ToolLibraryService.toolEffectLegend();
		return (
			<aside className="agent-toolbox__rail">
				<ToolsCallout
					icon={<Icon.Shield size={16} />}
					tone="success"
					title="Safe by default"
					description="Tools read and produce files. A change to QuickBooks, Drive, Gmail or Workflow is only ever proposed, for a person to execute."
				/>
				<section className="tk-card agent-toolbox__legend">
					<span className="agent-toolbox__legend-title">Tool types</span>
					{legend.map(effect => {
						const EffectIcon = EFFECT_ICONS[effect.effect] || Icon.Circle;
						return (
							<div key={effect.effect} className="agent-toolbox__legend-row">
								<span className={`tk-icon-tile tk-icon-tile--sm tk-icon-tile--${effect.tone}`}>
									<EffectIcon />
								</span>
								<span className="agent-toolbox__legend-text">
									<span className="agent-toolbox__legend-label">{effect.label}</span>
									{effect.detail}
								</span>
							</div>
						);
					})}
				</section>
				<ToolsCallout
					icon={<Icon.PlusCircle size={16} />}
					tone="accent"
					title="Need a new tool?"
					description="Tools are added by developers. Tell them what a playbook should be able to read or produce, and the skills that need it."
				/>
			</aside>
		);
	}

	renderNarrowing() {
		const { tools, search, category, effect, isOnlyUsed } = this.state;
		const categoryChoices = [{ value: "", label: "Any category" }, ...ToolLibraryService.toolCategoryChoices({ tools })];
		const effectChoices = [
			{ value: "", label: "Any access" },
			...ToolLibraryService.toolEffectLegend()
				.filter(item => item.isInUse)
				.map(item => ({ value: item.effect, label: item.label })),
		];
		const isNarrowed = this.isNarrowed();

		return (
			<div className="tk-toolbar">
				<SearchInput value={search} onChange={event => this.update({ search: event.target.value })} placeholder="Search tools" />
				<ToolsSelect filter label="Category" value={category} options={categoryChoices} onChange={value => this.update({ category: value })} />
				<ToolsSelect filter label="Access" value={effect} options={effectChoices} onChange={value => this.update({ effect: value })} />
				<ToolsCheckbox checked={isOnlyUsed} onChange={() => this.update({ isOnlyUsed: !isOnlyUsed })} label="Only tools a playbook uses" />
				{isNarrowed && (
					<span className="tk-toolbar__end">
						<button type="button" className="tk-text-action" onClick={this.onClearNarrowing}>
							Clear
						</button>
					</span>
				)}
			</div>
		);
	}

	render() {
		const { tools, isLoading, loadError, search, category, effect, isOnlyUsed } = this.state;
		const groups = ToolLibraryService.groupToolsBySource({ tools });
		const shownGroups = ToolLibraryService.toolGroupsMatching({ groups, search, category, effect, isOnlyUsed });

		return (
			<div className="agent-toolbox">
				<p className="tk-page-intro">
					The tools a playbook's skills can call. Each one says what it reads and what it can produce; developers add tools, skills and playbooks pick
					them up.
				</p>
				{isLoading && <div className="tk-empty tk-card">Loading the toolbox…</div>}
				{loadError && <div className="tk-notice tk-notice--danger">{loadError}</div>}
				{!isLoading && !loadError && (
					<React.Fragment>
						{this.renderNarrowing()}
						<div className="agent-toolbox__layout">
							<div className="agent-toolbox__groups">
								{shownGroups.length === 0 && (
									<div className="tk-empty tk-card">
										No tools match.{" "}
										<button type="button" className="tk-text-action" onClick={this.onClearNarrowing}>
											Show all {tools.length}
										</button>
									</div>
								)}
								{shownGroups.map(this.renderGroup)}
							</div>
							{this.renderRail()}
						</div>
					</React.Fragment>
				)}
			</div>
		);
	}
}

export default AgentToolbox;
